<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">Budget Landing Page</h1>
    <span class="banner py-3 mb-4">
      <div>This page will allow you to view your initial budget, plus any additional versions or reforecasts. It will show the status of each budget (Open, Pending Approval, Approved, Published, Archived) as well as the Version number, and your custom description which may be edited.</div>
      <br/>
      <div>
        At the top you will have the option to select your Property, Year and Accounting Method.
        <br/>
        You will have tabs at the top allowing you to filter for the type of budget desired: All budgets, only the Versions, Reforecasts, Approved, or Archived.
      </div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Import</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>The Import option will allow you to import a budget from excel in CSV (Comma Separated Value) format. If the status is Submitted for Approval, Pending Approved, Approved or Published, the import option will not be available.</div>
          <div>Once you select Import, the popup screen will describe the required data for each column. Your spreadsheet can have no formulas, titles, or totals. It <strong>must</strong> be formatted with the exact 14 columns shown below with no other data. If your spreadsheet does not follow these rules, you will receive errors when importing.</div>
          <div>The columns are:</div>
          <ul>
            <li><strong>Column 1</strong> - Your GL Account number formatted exactly like it is formatted within ResMan. If you have dashes or periods, you may have to format that column as "text" so that excel does not try to convert it into a formula.</li>
            <li><strong>Column 2</strong> - Your GL account names. It is acceptable if this column is blank, but the column must be there.</li>
            <li><strong>Column 3</strong> - The first month in your budget. If you are on a Calendar budget, this will be January.</li>
            <li><strong>Column 4 to 14</strong> - Months 2-12 of your Budget year.</li>
          </ul>
          <div><strong>Note:</strong> You can import rows that have all zeros without causing any problems.</div>
          <div><strong>Note:</strong> Any number that is opposite to what you normally see (like Vacancy Loss and Concessions) will be entered as a negative number.</div>
          <div>Here is an example of how your import spreadsheet should be formatted:</div>
          <img src="../../assets/help/1.png" />
          <br />
          <div><strong>Important Note:</strong></div>
          <div>When a budget is created, some GL Accounts will be automatically populated based on defaults from the Market Rent and Occupancy worksheets. Example GL Accounts are Gross Potential Rent, Vacancy Loss and Gain/Loss to Lease.</div>
          <div>When you Import budget information, only the GL Accounts in your import will be affected.</div>
          <div>Example: If you did not import anything for Gain/Loss To Lease, there will still be numbers in that GL Account created previously from the Occupancy worksheets.</div>
          <div>If you want every number in the budget to be overwritten, make sure to include every GL Account in your import, even if there are zero amounts in all of the columns.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Versions / Clone</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>It is not necessary to create multiple versions of your budget, but you may find it helpful.</div>
          <div>Possible scenarios for using versions are:</div>
          <ul>
            <li><strong>Example 1:</strong> Once the first user has completed their budget entries, you would create a new version, which would leave you a saved copy of that first version. The version descriptions would be similar to "Site Staff" or "Regional" (or even individual's names) to indicate who had entered the data before a new version was created.</li>
            <li><strong>Example 2:</strong> You may want to create multiple test scenarios. Each version would represent a scenario and your descriptions would describe that scenario.</li>
            <li><strong>Example 3:</strong> You may want to clone a new version just before you Submit for Approval and just before you Publish in case you need to return to the budget in one of those prior statuses.</li>
          </ul>
          <br />
          <div>To create a new version of a budget, click the ellipsis (3-dots) on the right side of your budget.</div>
          <img src="../../assets/help/2.png" />
          <br />
          <img src="../../assets/help/3.png" />
          <div>Once your new version has been created, edit the description to identify this new version by following these steps:</div>
          <div>Use the pencil (edit) icon to enter the new budget</div>
          <img src="../../assets/help/4.png" />
          <div>Click on Settings from your left-side menu</div>
          <img src="../../assets/help/5.png" />
          <div>Add your Description and click Save</div>
          <img src="../../assets/help/6.png" />
          <div>Your new description will now display on the Browse Budgets page.</div>
          <img src="../../assets/help/7.png" />
          <div>The version description may be edited at any time on the settings page. They will help you distinguish between your various budgets.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Add New Budget Workflow</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div><strong>+ New Budget (Step 1)</strong></div>
          <div>To create a New Budget, click +New Budget.</div>
          <div>This will bring you to the New Budget page which is the first of five steps to create your new budget. As you move along the steps, you will see the counter at the top progress to match your step.</div>
          <div>The New Budget step will allow you to establish some of your base budget information.</div>
          <div>Once you have completed the five steps to creating a New Budget and you choose CREATE, you will no longer be able to change these four choices.</div>
          <div>If you made an error in your choices, you can Archive this budget and create a new one.</div>
          <br />
          <div>These are the four fields available:</div>
          <ul>
            <li><strong>Property</strong> - Select the property that you would like to budget</li>
            <li><strong>Start Year</strong> - Select the Year that this budget should begin</li>
            <li>
              <div><strong>Start Month</strong> - Select the Month that this budget should begin. The first month of your accounting year will be provided for you, but you can change this month if desired.</div>
              <div><strong>Note:</strong> Your budget is identified by the last month of the budget. If your budget runs from July 2021 - June 2022 then it will be referred to as the 2022 budget on future screens.</div>
            </li>
            <li><strong>Accounting Basis</strong> - Select whether you would like to budget for Cash basis or Accrual Basis. You may create a separate budget for each one if desired.</li>
          </ul>
          <div><strong>Cancel</strong> will allow you to stop creating this budget and leave this screen.</div>
          <div><strong>Next</strong> will move you to step 2 of creating your new budget.</div>
          <br />

          <div><strong>General Settings (Step 2)</strong></div>
          <div>This is the second step of five steps and will allow you to set the following information.</div>
          <div><strong>Version Description</strong> - This field can be edited at any time. It will help you differentiate your various versions. You may want to start by identifying it as Version 1.</div>
          <div><strong>Note:</strong> The first budget created will automatically be Version 1. Clicking the Clone option will create a new version.</div>
          <div><strong>Template</strong> - The template that ResMan creates for you will default, but you can select any template that you may have created.</div>
          <div>The purpose for a template is to determine which GL Accounts will need a budget and present them in a logical fashion. ResMan recommends that you use the ResMan Default Budget Template as it will be organized to match your chart of accounts.</div>
          <div><strong>Estimate Future Historicals</strong> - When working on your budget, you may wish to view Historical data.</div>
          <div>For example:</div>
          <ul>
            <li>If you are working on your 2022 budget, you would want to see a full year of historical data for 2021.</li>
            <li>However, if it is August of 2021 when you are working on your budget, some months do not exist: August, September, October, November, and December.</li>
            <li>Therefore, we provide three options to fill in the missing months.</li>
            <li class="no-bullet">
              <ul>
                <li><strong>Estimate Future Historicals by annualizing current year (2021) actuals and start in _____ Month</strong></li>
                <li>This will take the prior current month actuals and average them and apply that as "historical" numbers starting in the month selected.</li>
                <li>For example, if you select this option and select August, the January through July actual numbers will be averaged. That average will be populated in August through December.</li>
              </ul>
              <ul>
                <li><strong>Estimate Future Historicals by using prior year (2020) actuals and start in _____ Month</strong></li>
                <li>For the month selected through the ending month of the budget, actual numbers from the prior year will be displayed.</li>
                <li>For example, if you are working on a 2022 budget, and you select this option and select August, the August through December numbers will be populated with actual historical numbers from 2020.</li>
              </ul>
              <ul>
                <li><strong>Estimate Future Historicals using current year (2021) budget and start in _____ Month</strong></li>
                <li>For the month selected through the ending month of the budget, budgeted numbers from the current budget year will be displayed.</li>
                <li>For example, if you select this option and select August, then August through December will have your current year budget numbers displayed.</li>
              </ul>
            </li>
          </ul>
          <div><strong>Approval Workflow</strong> - Initially no approval workflow will be selected. If you have created an approval workflow within Admin > Settings > Workflows for a budget, you will be able to select it here.</div>
          <div><strong>Note:</strong> You will be able to change the Approval Workflow setting here until the budget is Submitted for Approval or Approved. Then this setting will be locked.</div>
          <div><strong>Note:</strong> Even if you do not select an Approval Workflow, your budget will still need to be Approved before it can be Published.</div>
          <br />
          <div><strong>Back</strong> will take you to the New Budget (Step 1) page.</div>
          <div><strong>Next</strong> will move you to step 3 of creating your new budget.</div>
          <br />

          <div><strong>Lease Calculations (Step 3)</strong></div>
          <div>This third step is going to allow you to determine how your new leases and your renewal leases are priced.</div>
          <br />
          <div><strong>New Leases</strong> - You will have three choices regarding how you price your new leases.</div>
          <ul>
            <li><strong>1. New Leases will be given a percentage discount off of Market Rent (yes Gain/Loss to Lease)</strong></li>
            <li class="no-bullet">
              <ul>
                <li>
                  If this option is selected a new line will appear "The discount will be ___% of Market Rent". This percentage will populate the Occupancy > Actual Rent tab > New Move In Discount % line
                </li>
              </ul>
            </li>
            <li><strong>2. New Leases will be at Market Rent (no Gain/Loss to Lease)</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected, "MKT" will populate on the Occupancy > Actual Rent tab > New Move In Discount % line</li>
              </ul>
            </li>
            <li><strong>3. New Leases will be at Market Rent AND also offer a recurring concession (no Gain/Loss to Lease)</strong></li>
            <li class="no-bullet">
              <ul>
                <li>
                  If this option is selected a new line will appear: "This recurring concession will be ____% of Market Rent". You will select a single concession Transaction Category.
                </li>
                <li>
                  If this is selected, "MKT" will display on the Occupancy > Actual Rent tab > New Move In Discount % line. Also, if this is selected, the Occupancy > Rental Deductions tab > Recurring Move In Concessions line will initially load all of the Recurring Charge amounts for the Transaction Category selected as of the end of the prior budget year. These amounts are editable.
                </li>
              </ul>
            </li>
          </ul>
          <div><strong>Renewal Leases</strong> - You will have three choices regarding how you price your renewals.</div>
          <ul>
            <li><strong>1. Renewals will be given a percentage increase over existing rent</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear "The percentage increase will be ____% over existing rent"</li>
                <li>If this option is selected, the percentage entered will prefill on the Occupancy > Actual Rent tab > Renewal Increase % line</li>
              </ul>
            </li>
            <li><strong>2. Renewals will be at Market Rent</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected, "MKT" will prefill on the Occupancy > Actual Rent tab > Renewal Increase % line</li>
              </ul>
            </li>
            <li><strong>3. Renewals will be at Market Rent AND also offer a recurring concession</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear: "This recurring concession will be ____% of Market Rent and will use concession transaction category:"</li>
                <li>The user will select a single Transaction Category</li>
                <li>If this is selected, the Occupancy > Rental Deductions tab > Recurring Renewal Concessions will initially load all of the Recurring Charge amounts for the Transaction Category selected as of the end of the prior budget year. This is editable.</li>
              </ul>
            </li>
          </ul>
          <br />
          <div><strong>Back</strong> will take you to the General Settings (Step 2) page.</div>
          <div><strong>Next</strong> will move you to step 4 of creating your new budget.</div>
          <br />

          <div><strong>Concession & Bad Debt (Step 4)</strong></div>
          <div>This fourth step is going to allow you to select additional concessions and bad debt transaction categories.</div>
          <div><strong>Other Concessions</strong> - Any Transaction Categories selected on this drop-down will appear on the Occupancy > Rental Deductions > Other Concession screen and be available to you for budgeting.</div>
          <div><strong>Bad Debt</strong> - You have three options for calculating your bad debt write offs. If you choose one of the calculation methods (the first two options in the drop-down), the resulting numbers will appear in the Occupancy > Rental Deductions tab > Bad Debt line item. If you select Manual Entry, nothing will populate.</div>
          <ul>
            <li><strong>1. Bad Debt will be a percentage of the selected GL Account</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected a new line will appear: "This will be _____% of ________________________________"</li>
                <li>1st question: What is the percentage?</li>
                <li>2nd question: What is the GL Account?</li>
              </ul>
            </li>
            <li><strong>2. Bad Debt will be Average Market Rent multiplied by the number of Skips/Evictions</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected, no calculation sentence will appear, but the system will use Average Market Rent multiplied by the skips/evictions displayed on the Occupancy > Rental Deductions > Bad Debt section.</li>
                <li>Average Market Rent can be viewed on the Occupancy top summary section.</li>
              </ul>
            </li>
            <li><strong>3. Manual Entry</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected, no calculation sentence will appear</li>
                <li>Bad Debt Transaction Category: No matter which of the three choices were selected, there will be a final drop-down where you can select your Transaction Category for Bad Debt Write Off. Your Transaction Category description will appear on the Occupancy > Rental Deductions > Bad Debt line description.</li>
              </ul>
            </li>
          </ul>
          <br />
          <div><strong>Back</strong> will take you to the Lease Calculations (Step 3) page.</div>
          <div><strong>Next</strong> will move you to step 5 (Beginning Vacancies) of creating your new budget.</div>
          <br />

          <div><strong>Beginning Vacancies (Step 5)</strong></div>
          <div>This fifth step is going to allow you to determine your starting Vacant Unit count.</div>
          <div>Initially it will display zeros for each unit type. You can manually select a number for each unit type or you can click <strong>LOAD PROJECTED VACANCIES AS OF 12/31/21</strong>. The date will be the day before your budget starts.</div>
          <div>If you click this option to LOAD PROJECTED VACANCIES, it will take your vacancies as of "today" and add any preleases and subtract any Notice to Vacates that currently exist through the displayed date.</div>
          <div>You can select this option again later to update these vacancies later in the year.</div>
          <br />
          <div><strong>Back</strong> will take you to the Other Concessions and Bad Debt (Step 4) page.</div>
          <div><strong>Create</strong> will create the budget. To access your settings options later, they can be located on the left-side navigation options.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Budget Approvals</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div><strong>Approvals</strong></div>
          <div>If desired, the budget can be set up to be approved by multiple approvers. If an approval workflow is created, the budget must be approved at each step in the workflow. The possible approval statuses of a budget are Pending Approval, Disputed and Approved.</div>
          <div><strong>Create a Budget Workflow</strong></div>
          <div>To set up a budget approval workflow, follow the steps:</div>
          <ul>
            <li>
              <strong>Step 1.</strong> Create new Workflow Groups for each step of budget approval.
            </li>
            <li class="no-bullet">
              <ul>
                <li>Go to Admin > Groups > New Workflow Group.</li>
                <li>Name the Workflow group and assign approvers to each workflow group.</li>
              </ul>
            </li>
            <li>
              <strong>Step 2.</strong> Create a budget workflow.
            </li>
            <li class="no-bullet">
              <ul>
                <li>Go to Admin > Settings > Workflows and select "New".</li>
                <li>For the type, select Budget.</li>
                <li>Enter a name for this approval (Example: Budget Approvers)</li>
                <li>Add the number of desired steps in the budget approval workflow.</li>
                <li>
                  <img src="../../assets/help/8.png" />
                </li>
              </ul>
            </li>
          </ul>
          <div>Example of a Budget Approval Workflow:</div>
          <img src="../../assets/help/9.png" />
          <br />
          <div><strong>Approval Workflow Setup</strong></div>
          <div>Add the desired workflow to the budget</div>
          <ul>
            <li>Go to Accounting > BudgetPro > select a budget > Settings.</li>
            <li>Select the desired budget workflow from the "Approval Workflow" drop down</li>
            <li>
              <img src="../../assets/help/10.png" />
            </li>
          </ul>
          <br />
          <div><strong>Approving/Disputing a Budget</strong></div>
          <div>To start the approval process,</div>
          <ul>
            <li>Go to the Browse Budgets page.</li>
            <li>On the line for your version of the budget, click on the Ellipsis (3 dots) on the left side.</li>
            <li>Select <strong>Submit for Approval</strong>, which will place the budget into a Pending Approval status. It will display how many people have approved out of the total number of necessary approval levels.</li>
            <li class="no-bullet"><ul><li>Example: If 2 approval levels have been created, initially the status will display Pending Approval (0/2).</li></ul></li>
            <li>Once the budget is Pending Approval, the Ellipsis (3 dots) menu will offer options to <strong>Approve</strong> or <strong>Dispute</strong> this budget.</li>
            <li>Once the budget has been approved by at least one person in the workflow, the next person in the approval workflow can approve or dispute the budget.</li>
          </ul>
          <br />
          <div><strong>Submit For Approval</strong></div>
          <img src="../../assets/help/11.png" />
          <br />
          <div><strong>Approve or Dispute</strong></div>
          <img src="../../assets/help/12.png" />
          <br />
          <div><strong>Publish</strong></div>
          <img src="../../assets/help/13.png" />
          <br />
          <div><strong>Status History</strong></div>
          <img src="../../assets/help/14.png" />
          <br />
          <div><strong>Disputing A budget</strong></div>
          <ul>
            <li>Once a budget has been Submitted for Approval and before it has been fully Approved, Dispute can be selected.</li>
            <li>Once a budget has been Disputed, it is now available to be edited.</li>
            <li>Once the issue has been resolved, the budget will again be Submitted for Approval and will start the approval process over again.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(4)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(4)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Approval Notifications</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>It will be possible to set up email notifications for budget approvals. In your user settings, which can be accessed by clicking your name in the upper-right corner of the main ResMan screen, you will see three notifications related to budgets.</div>
          <div><strong>Approval Notification Settings</strong></div>
          <img src="../../assets/help/15.png" />
          <div>The email formats are located under Tools > Messaging > System Templates</div>
          <div><strong>Budget System Templates</strong></div>
          <img src="../../assets/help/16.png" />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(5)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(5)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Budget Status (Status History)</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div><strong>Workflow Approvals Activated</strong></div>
          <div>This chart will display the options available for each budget status <strong>if you have setup and selected to use a Budget Approval Workflow.</strong></div>
          <br />
          <div><strong>Options available based on Approval Status</strong></div>
          <img src="../../assets/help/17.png" />
          <br />
          <div><strong>Changes to the Approval Workflow steps:</strong></div>
          <div>If changes are made to the approval workflow steps, the budgets will incorporate those changes (even if they are mid-approvals). The system looks at which Rules/Steps have matching approvals, and which are still missing and uses those counts.</div>
          <div><strong>Example 1</strong></div>
          <ul>
            <li>Your workflow has 2 steps, and step 1 has been approved (current status: Pending 1/2).</li>
            <li>You determine step 2 is not necessary and remove it from the workflow.</li>
            <li>The budget becomes Pending 1/1 and can be immediately approved to final approval.</li>
          </ul>
          <div><strong>Example 2</strong></div>
          <ul>
            <li>Your workflow has 1 step, and it is Submitted for Approval (current status: Pending 0/1).</li>
            <li>You add a 2nd step in the approval workflow.</li>
            <li>Both steps will now be required.</li>
          </ul>
          <br />
          <div><strong>Workflow Approvals Not Activated</strong></div>
          <div>This chart will indicate what options are available for each budget status <strong>if you have not setup or selected to use an Approval Workflow.</strong></div>
          <img src="../../assets/help/18.png" />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(6)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(6)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Export</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>You will be able to export your budget numbers to excel. The Working Draft will provide the GL accounts in the format of a financial statement. It provides the twelve months of budget numbers in addition to the comparison columns shown on the Summary view of the GL page. It will offer the ability to export Calculations used and the Assumption statement.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(7)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(7)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Archive / Unarchive</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>The Archive function will allow you to hide a budget from the default Browse Budgets page. To Archive a budget click on the ellipsis (3 dots) on the right-side of the budget.</div>
          <img src="../../assets/help/19.png" />
          <div>You will be able to view archived budgets from the Archive menu choice on the Browse Budgets page.</div>
          <img src="../../assets/help/20.png" />
          <div>You will be able to Unarchive a budget. Once it is unarchived, it will be visible from the default Browse Budgets page.</div>
          <div>Note: The budget will be in the same status as it was when archived.</div>
          <div>For example: If it was partially approved, it will still be partially approved.</div>
          <img src="../../assets/help/21.png" />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(8)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(8)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Right-Side Icons and Ellipsis Menu Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>To the right of each version, you will have additional options on the right-side of the screen.</div>
          <img src="../../assets/help/22.png" />
          <div>
            <img class="small" src="../../assets/help/23.png" />
            <span class="pl-3">Will display if this version is editable. Click this to edit the budget.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/24.png" />
            <span class="pl-3">Will display if the version is not editable. Click to view the budget.</span>
            <br/>
            <span class="pl-12">The budget will not be editable if it is submitted for approval, approved, published or archived.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/25.png" />
            <span class="pl-3">The ellipsis menu will have various options, depending on the status of the budget.</span>
          </div>
          <ul>
            <li><strong>Clone</strong> - this will allow you to create a new version of your budget.</li>
            <li><strong>Export</strong> - this will allow you to export this budget to excel.</li>
            <li><strong>Submit For Approval</strong> - this will allow users with the correct permission to start the approval process.</li>
            <li><strong>Approve</strong> - this will allow users with the correct permission to approve. If you have multiple steps in your approval workflow, the number of approvers will display (1/3) shows one of the three steps has been approved.</li>
            <li><strong>Dispute</strong> - Allows the budget to be editable. It can be submitted for approval again afterwards.</li>
            <li><strong>Publish</strong> - Allows users with the correct permission to publish a fully approved budget.</li>
            <li><strong>Archive</strong> - Allows users with the correct permission to archive a budget that is no longer needed.</li>
            <li><strong>Status History</strong> - shows the history of the user and timestamp when the budget's status was changed. For approvals it also shows the details of the approval steps.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Budgets Landing')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'BudgetsLandingHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  img {
      max-width: 650px;
  }
  img.small {
      width: 35px;
  }
  .icon {
    order: 0;
  }

  .header {
    order: 1;
  }

  .help-container {
    border-bottom: 1px solid var(--v-resGrey-lighten1)
  }

  .help-content div,
  .help-content li {
    margin-top: 10px !important;
    margin-bottom: 10px !important;
  }

  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }

  .pointer {
    cursor: pointer !important;
  }

  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }

  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

  .separator:not(:empty)::after {
    margin-left: .25em;
  }
</style>
